import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
  OnDestroy,
  NgZone,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

export interface ButtonDropdownOption {
  value: string | number;
  label: string;
}

@Component({
  selector: "app-button-dropdown",
  templateUrl: "./button-dropdown.component.html",
  styleUrls: ["./button-dropdown.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule],
})
export class ButtonDropdownComponent implements OnDestroy {
  /** Leading font-awesome icon classes, e.g. "fa fa-building-o". Optional. */
  @Input() leadIcon?: string;
  /** Tints the leading icon with the accent (green) color. */
  @Input() leadIconAccent = false;
  /** Options as plain strings or `{ value, label }` objects. */
  @Input() options: (string | ButtonDropdownOption)[] = [];
  /** Currently selected value (matched against option values). */
  @Input() selectedValue: string | number | null = null;
  /** Static text rendered before the selected label, e.g. "Auto Refresh: ". */
  @Input() labelPrefix = "";
  /** Overrides the computed label entirely when provided. */
  @Input() displayLabel?: string;
  /** Fallback text shown when nothing is selected. */
  @Input() placeholder = "Select";
  /** Menu horizontal alignment relative to the button. */
  @Input() menuAlign: "left" | "right" = "left";
  /** Minimum button width (any CSS length). Defaults to the live page width. */
  @Input() minWidth = "220px";
  @Input() minHeight = "36px";
  @Input() disabled = false;
  /** Stretch the control to fill its parent width. */
  @Input() block = false;
  @Input() accentColor = "#009BCB";
  /** Soft background for the active item (defaults derived from accent when unset). */
  @Input() accentSoftColor?: string;
  /** Shows a search box at the top of the menu that filters options by label. */
  @Input() searchable = false;
  /** Placeholder for the search box (only used when `searchable`). */
  @Input() searchPlaceholder = "Search...";
  /** Max height of the (scrollable) options list when `searchable`. */
  @Input() menuMaxHeight = "260px";
  /**
   * Render the menu in `document.body` with fixed positioning so it is not
   * clipped by overflow/stacking contexts (e.g. table panels).
   */
  @Input() overlay = false;

  @Output() selectionChange = new EventEmitter<string | number>();
  /** Emitted when the menu is opened (not when closed). */
  @Output() menuOpen = new EventEmitter<void>();

  @ViewChild("menuPanel") menuPanel?: ElementRef<HTMLElement>;
  @ViewChild("triggerBtn") triggerBtn?: ElementRef<HTMLButtonElement>;

  open = false;
  searchTerm = "";
  menuStyle: Record<string, string> = {};

  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ngZone = inject(NgZone);
  private menuMountedInBody = false;
  private menuEl: HTMLElement | null = null;
  private removeScrollListeners: (() => void) | null = null;

  @HostBinding("class.ln-dropdown-host--block")
  get hostBlockClass(): boolean {
    return this.block || this.minWidth === "100%";
  }

  @HostBinding("style.--ln-dropdown-accent")
  get hostAccent(): string {
    return this.accentColor;
  }

  @HostBinding("style.--ln-dropdown-accent-soft")
  get hostAccentSoft(): string {
    if (this.accentSoftColor) {
      return this.accentSoftColor;
    }
    // Convert #RRGGBB to rgba(..., 0.12) for the soft active background.
    const hex = (this.accentColor || "").replace("#", "");
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    }
    return "rgba(0, 155, 203, 0.12)";
  }

  get normalizedOptions(): ButtonDropdownOption[] {
    return (this.options ?? []).map((option) =>
      typeof option === "string" ? { value: option, label: option } : option
    );
  }

  get filteredOptions(): ButtonDropdownOption[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.normalizedOptions;
    }
    return this.normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(term)
    );
  }

  get buttonLabel(): string {
    if (this.displayLabel != null) {
      return this.displayLabel;
    }
    const selected = this.normalizedOptions.find(
      (option) => option.value === this.selectedValue
    );
    const base = selected ? selected.label : this.placeholder;
    return `${this.labelPrefix}${base}`;
  }

  get isPlaceholder(): boolean {
    if (this.displayLabel != null) {
      return false;
    }
    return (
      this.selectedValue === null ||
      this.selectedValue === undefined ||
      this.selectedValue === ""
    );
  }

  ngOnDestroy(): void {
    this.teardownOverlay();
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    if (this.open) {
      this.close();
      return;
    }
    this.open = true;
    this.searchTerm = "";
    this.menuOpen.emit();
    if (this.overlay) {
      setTimeout(() => this.setupOverlay());
    }
  }

  close(): void {
    this.teardownOverlay();
    this.open = false;
    this.searchTerm = "";
  }

  select(option: ButtonDropdownOption): void {
    this.selectedValue = option.value;
    this.selectionChange.emit(option.value);
    this.teardownOverlay();
    this.open = false;
    this.searchTerm = "";
  }

  isActive(option: ButtonDropdownOption): boolean {
    return option.value === this.selectedValue;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    const target = event.target as Node | null;
    if (!target) {
      return;
    }
    const inHost = this.hostRef.nativeElement.contains(target);
    const inMenu =
      this.overlay &&
      this.menuPanel?.nativeElement?.contains(target);
    if (!inHost && !inMenu) {
      this.close();
    }
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    if (this.open && this.overlay) {
      this.positionOverlayMenu();
    }
  }

  private setupOverlay(): void {
    if (!this.open || !this.overlay) {
      return;
    }
    const menu =
      this.menuPanel?.nativeElement ??
      this.hostRef.nativeElement.querySelector<HTMLElement>(
        ".ln-dropdown__menu"
      );
    if (!menu) {
      requestAnimationFrame(() => this.setupOverlay());
      return;
    }
    this.menuEl = menu;
    menu.style.setProperty("--ln-dropdown-accent", this.hostAccent);
    menu.style.setProperty("--ln-dropdown-accent-soft", this.hostAccentSoft);
    if (!this.menuMountedInBody) {
      document.body.appendChild(menu);
      this.menuMountedInBody = true;
    }
    this.positionOverlayMenu();
    requestAnimationFrame(() => {
      if (this.open) {
        this.positionOverlayMenu();
      }
    });
    this.bindScrollListeners();
  }

  private getMenuElement(): HTMLElement | null {
    return this.menuPanel?.nativeElement ?? this.menuEl;
  }

  private positionOverlayMenu(): void {
    const menu = this.getMenuElement();
    const trigger =
      this.triggerBtn?.nativeElement ??
      this.hostRef.nativeElement.querySelector("button");
    if (!menu || !trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const viewportPadding = 8;
    const menuWidth = Math.max(menu.offsetWidth || 0, rect.width);

    let left =
      this.menuAlign === "right" ? rect.right - menuWidth : rect.left;
    left = Math.min(
      Math.max(viewportPadding, left),
      window.innerWidth - menuWidth - viewportPadding
    );

    let top = rect.bottom + gap;
    const menuHeight = menu.offsetHeight || 0;
    if (
      menuHeight > 0 &&
      top + menuHeight > window.innerHeight - viewportPadding &&
      rect.top - gap - menuHeight > viewportPadding
    ) {
      top = rect.top - gap - menuHeight;
    }

    const nextStyle = {
      position: "fixed",
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      minWidth: `${Math.round(rect.width)}px`,
      zIndex: "1100",
      right: "auto",
    };
    this.menuStyle = nextStyle;
    menu.style.position = nextStyle.position;
    menu.style.top = nextStyle.top;
    menu.style.left = nextStyle.left;
    menu.style.minWidth = nextStyle.minWidth;
    menu.style.zIndex = nextStyle.zIndex;
    menu.style.right = nextStyle.right;
  }

  private bindScrollListeners(): void {
    this.unbindScrollListeners();
    const handler = () => {
      if (!this.open) {
        return;
      }
      this.ngZone.run(() => this.positionOverlayMenu());
    };
    window.addEventListener("scroll", handler, true);
    this.removeScrollListeners = () => {
      window.removeEventListener("scroll", handler, true);
    };
  }

  private unbindScrollListeners(): void {
    this.removeScrollListeners?.();
    this.removeScrollListeners = null;
  }

  private teardownOverlay(): void {
    this.unbindScrollListeners();
    const menu = this.getMenuElement();
    if (this.menuMountedInBody && menu?.parentElement === document.body) {
      const anchor =
        this.hostRef.nativeElement.querySelector(".ln-dropdown") ??
        this.hostRef.nativeElement;
      anchor.appendChild(menu);
    }
    this.menuMountedInBody = false;
    this.menuEl = null;
    this.menuStyle = {};
  }
}
